import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommonModal } from '../../../../global/modal/common/common-modal';
import { MaterialSpinner } from '../../../../global/spinner/material-spinner';
import DefaultButton from '../../../../global/default-button/default-button';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { ISalesforceOrderRecordDTO } from '../../../../../api/salesforce-order.dao';
import { SalesforceOrderViewModel } from './salesforce-order.viewmodel';
import { SalesforceOrderViewDetails } from './view-salesforce-order-presentation';
import {
    EDIT_ORDER_BUTTON_TEXT,
    ERROR_MESSAGE,
    ERROR_TITLE,
    LOADING_TEXT,
    MODAL_TITLE,
    NO_ORDER_LINKED_TEXT,
    SUMMARY_CLIENT_DEPARTMENT_LABEL,
    SUMMARY_PROJECT_MANAGER_LABEL,
    SUMMARY_PROJECT_MANAGER_2_LABEL,
    SUMMARY_REGION_LABEL,
} from './salesforce-order.texts';
import './salesforce-order.scss';

interface IProps {
    vm: SalesforceOrderViewModel;
}

const EventSummaryLine = (props: { label: React.ReactNode; value: string }) => (
    <div className='salesforce-order__event-summary-row'>
        <span className='salesforce-order__event-summary-label'>{props.label}:</span>
        <span className='salesforce-order__event-summary-value'>{props.value}</span>
    </div>
);

const OrderHeader = (props: { vm: SalesforceOrderViewModel; showEditOrder: boolean }) => {
    const intl = useIntl();
    const name = props.vm.getEventName();
    const whenLines = props.vm.getEventWhenLines();
    if (!name && whenLines.length === 0 && !props.showEditOrder) {
        return null;
    }
    return (
        <div className='salesforce-order__event-header' data-test-id='salesforce-order-event-header'>
            <div className='salesforce-order__event-header-top'>
                {name && <h2 className='salesforce-order__event-name'>{name}</h2>}
                {props.showEditOrder && (
                    <DefaultButton
                        text={intl.formatMessage(EDIT_ORDER_BUTTON_TEXT)}
                        className='salesforce-order__edit-order'
                        dataTestId='salesforce-order-edit-order'
                        onClick={props.vm.openEditOrder}
                    />
                )}
            </div>
            {whenLines.map((line, index) => (
                <span className='salesforce-order__event-datetime' key={index}>{line}</span>
            ))}
            <div className='salesforce-order__event-summary' data-test-id='salesforce-order-event-summary'>
                <EventSummaryLine label={<FormattedMessage {...SUMMARY_REGION_LABEL} />} value={props.vm.getRegion()} />
                <EventSummaryLine label={<FormattedMessage {...SUMMARY_CLIENT_DEPARTMENT_LABEL} />} value={props.vm.getClientDepartment()} />
                <EventSummaryLine label={<FormattedMessage {...SUMMARY_PROJECT_MANAGER_LABEL} />} value={props.vm.getProjectManager()} />
                {props.vm.hasProjectManager2() && (
                    <EventSummaryLine label={<FormattedMessage {...SUMMARY_PROJECT_MANAGER_2_LABEL} />} value={props.vm.getProjectManager2()} />
                )}
            </div>
        </div>
    );
};

const ErrorState = () => (
    <div className='salesforce-order__error' data-test-id='salesforce-order-error'>
        <h3 className='salesforce-order__error-title'>
            <FormattedMessage {...ERROR_TITLE} />
        </h3>
        <p className='salesforce-order__error-message'>
            <FormattedMessage {...ERROR_MESSAGE} />
        </p>
    </div>
);

const LoadingState = () => (
    <div className='salesforce-order__loading' data-test-id='salesforce-order-loading'>
        <MaterialSpinner radius={48} color='#1a1a1a' stroke={3} visible={true} />
        <span className='salesforce-order__loading-text'>
            <FormattedMessage {...LOADING_TEXT} />
        </span>
    </div>
);

// No order in the response — show the response's own message, or the default if it carried none.
const NoOrderState = (props: { message: string | null }) => (
    <div className='salesforce-order__no-data' data-test-id='salesforce-order-no-data'>
        {props.message ? props.message : <FormattedMessage {...NO_ORDER_LINKED_TEXT} />}
    </div>
);

const SalesforceOrderModal = mobxReact.observer((props: IProps) => {
    const { vm } = props;
    const intl = useIntl();

    if (!vm) {
        return null;
    }

    const orderDto = vm.getOrder();
    const record: ISalesforceOrderRecordDTO | null = orderDto?.order ?? null;

    const baseTitle = intl.formatMessage(MODAL_TITLE);
    const oeNumber = vm.getOeNumber();
    const title = oeNumber ? `${baseTitle} #: OE ${oeNumber}` : baseTitle;

    const renderContent = () => {
        if (vm.isLoading) {
            return <LoadingState />;
        }
        if (vm.hasError) {
            return <ErrorState />;
        }
        if (vm.hasNoOrder) {
            return <NoOrderState message={vm.getNoOrderMessage()} />;
        }
        if (orderDto && record) {
            return (
                <>
                    <OrderHeader vm={vm} showEditOrder={!!record && hasPermission(Permission.UPDATE_ORDER)} />
                    <SalesforceOrderViewDetails order={orderDto} />
                </>
            );
        }
        return null;
    };

    return (
        <CommonModal
            isOpen={vm.isOpen}
            onRequestClose={vm.close}
            title={title}
            className='salesforce-order-modal'
        >
            <div className='salesforce-order__content'>
                {renderContent()}
            </div>
        </CommonModal>
    );
});

export { SalesforceOrderModal };
